import { useEffect, useState } from 'react';

// @mui
import { Box, SwipeableDrawer, Typography, IconButton, FormGroup, FormControlLabel, Checkbox, TextField, Button } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { clearCreateAiReportLS, postCreateAiReport } from '../../../../reducers/Profile';
import { LoadingStatus } from '../../../../constants/redux.constants';
import { clearNotification, showNotification } from '../../../../reducers/App';

// style
import { styled } from '../../../../theme';

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * @param {Object} props
 * @param {boolean} props.openedReportId - ID отчета (строка или число), если не null, то открывается drawer
 * @param {import("react").Dispatch<import("react").SetStateAction<number | string | null>>} props.setReportId - Функция изменения ID отчета
 * @param {string} props.report_uri - Ссылка на отчет
 */
const MakeAiReportModal = ({ openedReportId, setReportId, report_uri }) => {

    const dispatch = useDispatch();

    const { createAiReportLS } = useSelector(state => state.profile);

    const [email, setEmail] = useState("");
    const [reportTypes, setReportTypes] = useState({ manager_report: false, resident_report: false, });

    const [errors, setErrors] = useState({});

    const handleSubmit = () => {

        const newErrors = {};

        if (!emailRegex.test(email)) {
            newErrors.email = 'Введите корректный email';
        }

        if (!reportTypes.manager_report && !reportTypes.resident_report) {
            newErrors.reportTypes = 'Выберите хотя бы один тип отчета';
        }

        if (newErrors.email || newErrors.reportTypes) {
            setErrors(newErrors);
            return;
        }

        dispatch(postCreateAiReport({ report_uri, email, report_types: Object.keys(reportTypes).filter(key => reportTypes[key]).join('|') }));

    }

    useEffect(() => {

        if (createAiReportLS === LoadingStatus.SUCCEEDED) {
            dispatch(showNotification({ isOpen: true, message: 'Запрос успешно отправлен.', severity: 'success' }));
        }

        if (createAiReportLS === LoadingStatus.FAILED) {
            dispatch(showNotification({ isOpen: true, message: 'При отправке запроса произошла ошибка.', severity: 'error' }));
        }

    }, [createAiReportLS]);

    useEffect(() => {

        // очищаем состояние при закрытии drawer'а
        return () => {
            dispatch(clearCreateAiReportLS());
            dispatch(clearNotification());
        }

    }, [openedReportId]);

    return (
        <SwipeableDrawer
            anchor='right'
            open={!!openedReportId}
            onClose={() => { setReportId(null) }}
            onOpen={() => { }}
            PaperProps={{
                sx: { width: { xs: '100%', md: "35vw" } },
                elevation: 5,
            }}
        >

            <Box display='flex' flexDirection='column' justifyContent='space-between' sx={{ p: { xs: '20px 20px', md: '30px 50px' }, height: '100%', overflowY: 'auto' }}>

                <Box display='flex' flexDirection='column' gap={6.5}>

                    <Box display='flex' justifyContent='flex-end'>
                        <IconButton onClick={() => setReportId(null)}>
                            <CloseIcon sx={{ width: '24px', height: '24px', color: 'grey.scale70' }} />
                        </IconButton>
                    </Box>

                    <Box display='flex' flexDirection='column' gap={4}>

                        <Box display='flex' flexDirection='column' gap={2.25}>

                            <Typography fontSize='2rem' fontWeight={500}>ИИ-отчет</Typography>

                            <Typography fontSize='0.875rem' color='grey.scale70'>Выберите необходимые типы отчетов и укажите почту</Typography>

                            <Box display='flex' gap={2} sx={{ backgroundColor: 'grey.scale10', borderRadius: '20px', p: '20px 20px' }}>

                                <InfoOutlinedIcon sx={{ color: 'grey.scale50', cursor: 'default' }} />

                                <Box display='flex' flexDirection='column' gap={0.5}>
                                    <Typography fontSize='0.875rem' fontWeight={700}>Отчет будет создан в течение 24 часов</Typography>
                                    <Typography fontSize='0.875rem'>Ваш запрос сразу поступает в обработку. Пожалуйста, не запрашивайте отчет повторно</Typography>
                                </Box>

                            </Box>

                        </Box>

                        <Box display='flex' flexDirection='column' gap={1.25}>

                            <Box display='flex' flexDirection='column'>

                                <Typography fontSize='0.875rem' color='grey.scale70'>Тип отчета*</Typography>

                                {
                                    errors.reportTypes && <Typography fontSize='0.75rem' color='red'>{errors.reportTypes}</Typography>
                                }

                            </Box>

                            <FormGroup>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={reportTypes.manager_report}
                                            defaultChecked
                                            onChange={(e) => {
                                                setErrors((prevErrors) => ({ ...prevErrors, reportTypes: undefined }));
                                                setReportTypes((prev) => ({ ...prev, manager_report: e.target.checked }))
                                            }}
                                            sx={{
                                                color: 'grey.scale50',
                                                '&.Mui-checked': {
                                                    color: 'purple.dark',
                                                },
                                            }}
                                        />
                                    }
                                    label={<Typography fontSize='0.875rem'>Отчет руководителя</Typography>}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={reportTypes.resident_report}
                                            onChange={(e) => {
                                                setErrors((prevErrors) => ({ ...prevErrors, reportTypes: undefined }));
                                                setReportTypes((prev) => ({ ...prev, resident_report: e.target.checked }))
                                            }}
                                            sx={{
                                                color: 'grey.scale50',
                                                '&.Mui-checked': {
                                                    color: 'purple.dark',
                                                },
                                            }}
                                        />
                                    }
                                    label={<Typography fontSize='0.875rem'>Отчет для жителя</Typography>}
                                />

                            </FormGroup>

                        </Box>

                        <Box display='flex' flexDirection='column' gap={1}>

                            <Typography fontSize='0.875rem' color='grey.scale70'>Почта*</Typography>

                            <TextField
                                fullWidth
                                placeholder='my@email.ru'
                                error={!!errors?.email}
                                helperText={errors?.email || ''}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (errors?.email) {
                                        setErrors((prevErrors) => ({ ...prevErrors, email: undefined }));
                                    }
                                }}
                                size="small"
                                sx={{ ...styled('autocomplete', "input", 'white'), '& .MuiInputBase-input': { cursor: 'pointer' } }}
                            />

                        </Box>

                    </Box>

                </Box>

                <Box display='flex' gap={1.25} sx={{ width: { xs: '100%', md: '50%' } }}>

                    <Button
                        color='green'
                        variant='contained'
                        fullWidth
                        disabled={createAiReportLS === LoadingStatus.PENDING}
                        onClick={handleSubmit}
                    >
                        <Typography fontSize='0.875rem'>Создать</Typography>
                    </Button>

                    <Button color='grey' variant='outlined' fullWidth onClick={() => setReportId(null)}>
                        <Typography fontSize='0.875rem'>Отмена</Typography>
                    </Button>

                </Box>

            </Box>

        </SwipeableDrawer>
    )

}

export default MakeAiReportModal